import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Sparkles, UserRound, ArrowRight } from "lucide-react";
import Uploader from "../components/Uploader";
import ResultCard from "../components/ResultCard";

const NAME_KEY = "idiag-firstname";

function NameGate({ onReady }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const v = name.trim();
    if (!v) return;
    try {
      localStorage.setItem(NAME_KEY, v);
    } catch {
      /* noop */
    }
    onReady(v);
  };
  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-3xl border border-border/70 bg-card/60 p-8 sm:p-12 max-w-xl mx-auto text-center"
      data-testid="name-gate"
    >
      <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/15 flex items-center justify-center mb-4">
        <UserRound className="w-7 h-7 text-primary" />
      </div>
      <h2 className="text-2xl font-bold">{t("gate.title")}</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-6">{t("gate.subtitle")}</p>
      <label className="sr-only" htmlFor="firstname">
        {t("gate.label")}
      </label>
      <input
        id="firstname"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("gate.placeholder")}
        data-testid="name-gate-input"
        className="w-full h-12 rounded-xl border border-input bg-transparent px-4 text-center text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      />
      <button
        type="submit"
        disabled={!name.trim()}
        data-testid="name-gate-submit"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-primary text-primary-foreground font-semibold hover:brightness-110 transition disabled:opacity-50"
      >
        {t("gate.start")}
        <ArrowRight className="w-4 h-4" />
      </button>
    </motion.form>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState(() => {
    try {
      return localStorage.getItem(NAME_KEY) || "";
    } catch {
      return "";
    }
  });

  const changeName = () => {
    try {
      localStorage.removeItem(NAME_KEY);
    } catch {
      /* noop */
    }
    setFirstName("");
  };

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-24">
      {!result && (
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 h-7 px-3.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-6"
            data-testid="hero-badge"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t("hero.badge")}
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]"
            data-testid="hero-title"
          >
            {t("hero.title")}
          </h1>
          <p
            className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t("hero.subtitle")}
          </p>
        </motion.section>
      )}

      <section className="mt-10 sm:mt-14">
        {result ? (
          <ResultCard data={result} onReset={() => setResult(null)} />
        ) : !firstName ? (
          <NameGate onReady={setFirstName} />
        ) : (
          <>
            {!loading && (
              <div
                className="max-w-2xl mx-auto mb-3 flex items-center justify-center gap-2 text-sm text-muted-foreground"
                data-testid="gate-greeting"
              >
                <UserRound className="w-4 h-4 text-primary" />
                <span>
                  {t("gate.greeting")}, <span className="font-semibold text-foreground">{firstName}</span>
                </span>
                <button
                  onClick={changeName}
                  className="underline underline-offset-2 hover:text-foreground transition"
                  data-testid="gate-change"
                >
                  {t("gate.change")}
                </button>
              </div>
            )}
            <Uploader
              firstName={firstName}
              onResult={setResult}
              isLoading={loading}
              setIsLoading={setLoading}
            />
          </>
        )}
      </section>
    </main>
  );
}
